/**
 * Hook for calling the UDE NLQ backend endpoints.
 *
 * Provides `translate` (natural language → Query) and `summarize`
 * (diff two queries → human-readable summary) functions with loading / error state.
 */
import { useCallback, useState } from 'react';
import api from 'core/util/api';
import type { Query } from '@kentik/ui-shared/query/query.types';

interface NlqState {
  isLoading: boolean;
  error: string | undefined;
  translate: (prompt: string, currentQuery?: Query) => Promise<Query>;
  summarize: (previousConfig: unknown, currentConfig: unknown) => Promise<string>;
  generateTitle: (query: Query) => Promise<string>;
  clearError: () => void;
}

interface NlqTranslateResponse {
  query: Query;
  model: string;
  duration_ms: number;
}

interface NlqSummarizeResponse {
  summary: string;
  model: string;
}

interface NlqTitleResponse {
  title: string;
  model: string;
}

export function useUdeNlq(): NlqState {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const clearError = useCallback(() => setError(undefined), []);

  const translate = useCallback(async (prompt: string, currentQuery?: Query): Promise<Query> => {
    setIsLoading(true);
    setError(undefined);

    try {
      const response: NlqTranslateResponse = await api.post('/api/ui/ude/nlq', {
        body: { prompt, currentQuery }
      });
      return response.query;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to translate query';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const summarize = useCallback(async (previousConfig: unknown, currentConfig: unknown): Promise<string> => {
    try {
      const response: NlqSummarizeResponse = await api.post('/api/ui/ude/nlq/summarize', {
        body: { previousConfig, currentConfig }
      });
      return response.summary;
    } catch {
      return 'Manual change';
    }
  }, []);

  const generateTitle = useCallback(async (query: Query): Promise<string> => {
    try {
      const response: NlqTitleResponse = await api.post('/api/ui/ude/nlq/title', {
        body: { query }
      });
      return response.title;
    } catch {
      const measurement = query.measurement?.split('/').pop() || 'Query';
      const metricCount = query.metrics?.length || 0;
      return `${measurement} - ${metricCount} metric${metricCount !== 1 ? 's' : ''}`;
    }
  }, []);

  return { isLoading, error, translate, summarize, generateTitle, clearError };
}
